// src/components/Admin/SchoolManager.jsx
import React, { useEffect, useState } from 'react';
import { CheckCircle2, ShieldCheck, School, UserCog } from 'lucide-react';
import { collection, doc, getDocs, serverTimestamp, updateDoc } from 'firebase/firestore';
import { signInWithCustomToken } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { FIELD_DEFINITIONS } from '../../context/AuthContext';
import { auth, db, functions } from '../../firebase/config';
import Spinner from '../Common/Spinner';
import { showConfirmDialog, showToast } from '../../utils/feedback';

const defaultForm = { schoolName: '', adminEmail: '', adminPassword: '' };

const ALWAYS_ENABLED_KEYS = ['student_name', 'std_roman', 'photo_name', 'photo_url'];

const buildDefaultPermissions = () =>
  FIELD_DEFINITIONS.reduce((acc, field) => {
    acc[field.key] = !!field.mandatory || ALWAYS_ENABLED_KEYS.includes(field.key);
    return acc;
  }, {});

const SchoolManager = () => {
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [clearingStatusId, setClearingStatusId] = useState(null);
  const [loggingInAsSchoolId, setLoggingInAsSchoolId] = useState(null);
  const [formData, setFormData] = useState(defaultForm);
  const [fieldPermissions, setFieldPermissions] = useState(buildDefaultPermissions());

  const loadSchools = async () => {
    setLoading(true);
    try {
      const snap = await getDocs(collection(db, 'schools'));
      const schoolList = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setSchools(schoolList);
    } catch (error) {
      console.error('Error loading schools:', error);
      showToast({ icon: 'error', title: error?.message || 'Failed to load schools.' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSchools();
  }, []);

  const toggleDefaultPermission = (key) => {
    if (ALWAYS_ENABLED_KEYS.includes(key)) return;
    setFieldPermissions((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const resetForm = () => {
    setFormData(defaultForm);
    setFieldPermissions(buildDefaultPermissions());
  };

  const handleCreateSchool = async (e) => {
    e.preventDefault();

    const schoolName = formData.schoolName.trim();
    const adminEmail = formData.adminEmail.trim();
    const adminPassword = formData.adminPassword.trim();

    if (!schoolName || !adminEmail || !adminPassword) {
      showToast({ icon: 'error', title: 'Please fill all fields.' });
      return;
    }

    if (adminPassword.length < 6) {
      showToast({ icon: 'error', title: 'Temporary password must be at least 6 characters.' });
      return;
    }

    const finalPermissions = {
      ...fieldPermissions,
      student_name: true,
      std_roman: true,
      photo_name: true,
      photo_url: true,
    };

    try {
      setSaving(true);
      const createSchoolUser = httpsCallable(functions, 'createSchoolUser');
      const result = await createSchoolUser({
        email: adminEmail,
        password: adminPassword,
        schoolName,
        fieldPermissions: finalPermissions,
      });

      const schoolId = result?.data?.schoolId;

      if (!schoolId) {
        throw new Error('School user was created, but no schoolId was returned.');
      }

      showToast({ icon: 'success', title: 'School created and login assigned.' });
      resetForm();
      await loadSchools();
    } catch (error) {
      console.error('Create school error:', error);
      showToast({
        icon: 'error',
        title: `${error?.code || 'unknown'}: ${error?.message || 'Failed to create school.'}`,
      });
    } finally {
      setSaving(false);
    }
  };

  const handleToggleSchoolField = async (school, fieldKey) => {
    if (ALWAYS_ENABLED_KEYS.includes(fieldKey)) return;

    const targetSchoolId = school.schoolId || school.id;
    const nextPermissions = {
      ...(school.fieldPermissions || {}),
      [fieldKey]: !school.fieldPermissions?.[fieldKey],
      student_name: true,
      std_roman: true,
      photo_name: true,
      photo_url: true,
    };

    try {
      await updateDoc(doc(db, 'users', targetSchoolId), {
        fieldPermissions: nextPermissions,
        updatedAt: serverTimestamp(),
      });

      await updateDoc(doc(db, 'schools', school.id), {
        fieldPermissions: nextPermissions,
        updatedAt: serverTimestamp(),
      });

      await loadSchools();
      showToast({ icon: 'success', title: 'Permissions updated.' });
    } catch (error) {
      console.error('Permission update error:', error);
      showToast({ icon: 'error', title: error?.message || 'Failed to update permissions.' });
    }
  };


  const handleClearStatus = async (school) => {
    const targetSchoolId = school.schoolId || school.id;
    const result = await showConfirmDialog({
      title: 'Clear update status?',
      html: `
        <div class="text-start">
          <p><strong>School:</strong> ${school.schoolName || '-'}</p>
          <p class="text-warning mb-0">This will clear the update status for every student in this school.</p>
        </div>
      `,
      icon: 'warning',
      confirmButtonText: 'Clear Status',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#d97706',
    });

    if (!result.isConfirmed) return;

    try {
      setClearingStatusId(school.id);
      const clearSchoolStudentUpdateStatus = httpsCallable(
        functions,
        'clearSchoolStudentUpdateStatus'
      );
      const response = await clearSchoolStudentUpdateStatus({ schoolId: targetSchoolId });
      const clearedStudents = response?.data?.clearedStudents ?? 0;

      showToast({
        icon: 'success',
        title: `Update status cleared for ${clearedStudents} student(s).`,
      });
    } catch (error) {
      console.error('Clear update status error:', error);
      showToast({
        icon: 'error',
        title: error?.message || 'Failed to clear student update status.',
      });
    } finally {
      setClearingStatusId(null);
    }
  };

  const handleDeleteSchool = async (school) => {
    const targetSchoolId = school.schoolId || school.id;
    const result = await showConfirmDialog({
      title: 'Delete this school?',
      html: `
        <div class="text-start">
          <p><strong>School:</strong> ${school.schoolName || '-'}</p>
          <p><strong>Email:</strong> ${school.adminEmail || '-'}</p>
          <p class="text-danger mb-0">This will remove the school data from Firestore.</p>
        </div>
      `,
      icon: 'warning',
      confirmButtonText: 'Delete School',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#dc2626',
    });

    if (!result.isConfirmed) return;

    try {
      setDeletingId(school.id);
      const deleteSchoolCascade = httpsCallable(functions, 'deleteSchoolCascade');
      const response = await deleteSchoolCascade({ schoolId: targetSchoolId });
      const deletedStudents = response?.data?.deletedStudents ?? 0;
      showToast({
        icon: 'success',
        title: `School deleted. Removed ${deletedStudents} student(s).`,
      });
      await loadSchools();
    } catch (error) {
      console.error('Delete school error:', error);
      showToast({ icon: 'error', title: error?.message || 'Failed to delete school.' });
    } finally {
      setDeletingId(null);
    }
  };

  const handleLoginAsSchool = async (school) => {
    const targetSchoolId = school.schoolId || school.id;
    const result = await showConfirmDialog({
      title: 'Login as this school?',
      html: `
        <div class="text-start">
          <p><strong>School:</strong> ${school.schoolName || '-'}</p>
          <p><strong>Email:</strong> ${school.adminEmail || '-'}</p>
          <p class="text-warning mb-0">Your current admin session will be replaced by this school user session.</p>
        </div>
      `,
      icon: 'question',
      confirmButtonText: 'Login as School',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#2563eb',
    });

    if (!result.isConfirmed) return;

    try {
      setLoggingInAsSchoolId(school.id);
      const loginAsSchool = httpsCallable(functions, 'loginAsSchool');
      const response = await loginAsSchool({ schoolId: targetSchoolId });
      const token = response?.data?.token;

      if (!token) throw new Error('No custom token was returned.');

      await signInWithCustomToken(auth, token);
      showToast({
        icon: 'success',
        title: `Logged in as ${school.schoolName}. Redirecting...`,
      });
      window.location.href = '/dashboard';
    } catch (error) {
      console.error('Login as school error:', error);
      showToast({
        icon: 'error',
        title: `${error?.code || 'unknown'}: ${error?.message || 'Failed to login as school.'}`,
      });
    } finally {
      setLoggingInAsSchoolId(null);
    }
  };

  return (
    <div className="space-y-4">
      <div className="app-panel mb-4">
        <div className="app-panel-header">
          <h3 className="d-flex align-items-center gap-2">
            <School size={18} /> Add New School
          </h3>
          <p>Create school logins and define which student fields are visible.</p>
        </div>

        <form onSubmit={handleCreateSchool}>
          <div className="row g-3">
            <div className="col-lg-4">
              <label className="form-label">School Name</label>
              <input
                className="form-control app-input"
                value={formData.schoolName}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, schoolName: e.target.value }))
                }
                required
              />
            </div>

            <div className="col-lg-4">
              <label className="form-label">Login Email</label>
              <input
                type="email"
                className="form-control app-input"
                value={formData.adminEmail}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, adminEmail: e.target.value }))
                }
                required
              />
            </div>

            <div className="col-lg-4">
              <label className="form-label">Temporary Password</label>
              <input
                type="password"
                className="form-control app-input"
                value={formData.adminPassword}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, adminPassword: e.target.value }))
                }
                minLength={6}
                required
              />
              <small className="text-muted">Password must be at least 6 characters.</small>
            </div>
          </div>

          <div className="permissions-card mt-4">
            <div className="d-flex align-items-center gap-2 mb-3 fw-semibold">
              <ShieldCheck size={18} /> Accessible Student Fields
            </div>

            <div className="row g-2">
              {FIELD_DEFINITIONS.map((field) => (
                <div className="col-lg-3 col-md-4 col-sm-6" key={field.key}>
                  <label className="permission-chip">
                    <input
                      type="checkbox"
                      checked={!!fieldPermissions[field.key]}
                      disabled={field.mandatory || ALWAYS_ENABLED_KEYS.includes(field.key)}
                      onChange={() => toggleDefaultPermission(field.key)}
                    />
                    <span>
                      {field.label}
                      {(field.mandatory || ALWAYS_ENABLED_KEYS.includes(field.key)) ? ' *' : ''}
                    </span>
                    {!!fieldPermissions[field.key] && <CheckCircle2 size={14} />}
                  </label>
                </div>
              ))}
            </div>

            <small className="text-muted d-block mt-2">
              Student Name, Standard, Photo Name, and Photo URL are always enabled.
            </small>
          </div>

          <button
            className="btn btn-primary app-primary-btn mt-4 px-4 rounded-pill"
            type="submit"
            disabled={saving}
          >
            {saving ? (
              <span className="d-inline-flex align-items-center gap-2">
                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                Creating...
              </span>
            ) : (
              'Create School Login'
            )}
          </button>
        </form>
      </div>

      <div className="app-panel">
        <div className="app-panel-header">
          <h3 className="d-flex align-items-center gap-2">
            <UserCog size={18} /> Schools & Permissions
          </h3>
          <p>Manage enabled fields, switch into school sessions, or delete a school.</p>
        </div>

        {loading ? (
          <div className="py-4">
            <Spinner label="Loading schools..." />
          </div>
        ) : schools.length === 0 ? (
          <div className="text-muted">No schools found.</div>
        ) : (
          <div className="table-responsive">
            <table className="table app-table align-middle">
              <thead>
                <tr>
                  <th>School</th>
                  <th>Email</th>
                  <th>Enabled Fields</th>
                  <th style={{ width: '240px' }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {schools.map((school) => (
                  <tr key={school.id}>
                    <td>{school.schoolName}</td>
                    <td>{school.adminEmail}</td>
                    <td>
                      <div className="d-flex flex-wrap gap-2">
                        {FIELD_DEFINITIONS.map((field) => {
                          const enabled =
                            !!school.fieldPermissions?.[field.key] ||
                            field.mandatory ||
                            ALWAYS_ENABLED_KEYS.includes(field.key);

                          return (
                            <label
                              key={field.key}
                              className={`permission-chip compact ${enabled ? 'enabled' : ''}`}
                            >
                              <input
                                type="checkbox"
                                checked={enabled}
                                disabled={field.mandatory || ALWAYS_ENABLED_KEYS.includes(field.key)}
                                onChange={() => handleToggleSchoolField(school, field.key)}
                              />
                              <span>
                                {field.label}
                                {(field.mandatory || ALWAYS_ENABLED_KEYS.includes(field.key)) ? ' *' : ''}
                              </span>
                            </label>
                          );
                        })}
                      </div>
                    </td>
                    <td>
                      <div className="d-flex gap-2 flex-wrap">
                        <button
                          type="button"
                          className="btn btn-sm btn-primary rounded-pill px-3"
                          disabled={loggingInAsSchoolId === school.id}
                          onClick={() => handleLoginAsSchool(school)}
                        >
                          {loggingInAsSchoolId === school.id ? (
                            <span className="d-inline-flex align-items-center gap-2">
                              <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                              Logging in...
                            </span>
                          ) : (
                            'Login as School'
                          )}
                        </button>


                        <button
                          type="button"
                          className="btn btn-sm btn-outline-warning rounded-pill px-3"
                          disabled={clearingStatusId === school.id}
                          onClick={() => handleClearStatus(school)}
                        >
                          {clearingStatusId === school.id ? (
                            <span className="d-inline-flex align-items-center gap-2">
                              <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                              Clearing...
                            </span>
                          ) : (
                            'Clear Status'
                          )}
                        </button>

                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger rounded-pill px-3"
                          disabled={deletingId === school.id}
                          onClick={() => handleDeleteSchool(school)}
                        >
                          {deletingId === school.id ? (
                            <span className="d-inline-flex align-items-center gap-2">
                              <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                              Deleting...
                            </span>
                          ) : (
                            'Delete'
                          )}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default SchoolManager;